import { useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";

import { setOperatorAccessToken, setOperatorAuthExpiredHandler } from "@/api/operator-graph";

import { getSigninState } from "./return-to";

/** Bind once inside AuthProvider, above routed components that issue queries. */
export function useOperatorAuthFetch(): void {
  const auth = useAuth();
  const accessToken = auth.user?.access_token ?? null;
  const applied = useRef<string | null>(null);
  if (applied.current !== accessToken) {
    applied.current = accessToken;
    setOperatorAccessToken(accessToken);
  }
  const authRef = useRef(auth);
  authRef.current = auth;

  useEffect(() => {
    let mounted = true;
    setOperatorAuthExpiredHandler(async () => {
      try {
        const user = await authRef.current.signinSilent();
        if (!mounted) return;
        // react-oidc-context catches UserManager errors and returns null.
        // A resolved promise by itself does not prove renewal succeeded.
        if (user !== null && user !== undefined && user.expired === false && user.access_token) {
          setOperatorAccessToken(user.access_token);
          return;
        }
      } catch {
        // Custom providers may still reject; use the same terminal path.
      }
      if (!mounted) return;
      setOperatorAccessToken(null);
      try {
        await authRef.current.signinRedirect({ state: getSigninState() });
      } catch {
        // RequireOperatorAuth displays provider errors; do not loop here.
      }
    });
    return () => {
      mounted = false;
      setOperatorAuthExpiredHandler(null);
      setOperatorAccessToken(null);
    };
  }, []);
}
